import { supabase } from "@/lib/supabase/client"
import { AuthChangeEvent, Session } from "@supabase/supabase-js"
import { invalidateSessionCache } from "@/components/session/session-guards"

/**
 * Sign in with email and password
 * Invalidates the session cache before and after so the new user's
 * organization data is fetched fresh.
 */
export const signInWithPassword = async (email: string, password: string) => {
  invalidateSessionCache()

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (!error) {
    invalidateSessionCache()
  }

  return {
    success: !error,
    data,
    error: error?.message,
  }
}

/**
 * Sign up with email and password
 * Adds metadata and configures redirect URL. The Turnstile token rides in
 * user metadata because that is the only channel the Before User Created auth
 * hook (platform-db before-user-created-hook) can read; the hook rejects
 * source="web" signups whose token fails Cloudflare siteverify.
 *
 * The confirmation email uses a token_hash link to /auth/callback (see
 * platform-db confirmation.html), which ignores emailRedirectTo. We still set it
 * to /auth/callback so any ConfirmationURL fallback lands on the unified callback
 * rather than dumping the user on /sign-in to re-enter their password.
 */
export const signUp = async (
  email: string,
  password: string,
  captchaToken: string,
) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
      data: {
        source: "web",
        captcha_token: captchaToken,
      },
    },
  })

  return {
    success: !error,
    data,
    emailSent: !error,
    error: error?.message,
  }
}

/**
 * Resend the signup confirmation email for an unconfirmed account
 */
export const resendConfirmation = async (email: string) => {
  const { error } = await supabase.auth.resend({
    type: "signup",
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  })

  return {
    success: !error,
    error: error?.message,
  }
}

/**
 * Send password reset email
 * Configures redirect URL and returns structured result
 */
export const resetPasswordForEmail = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/update-password`,
  })

  return {
    success: !error,
    emailSent: !error,
    error: error?.message,
  }
}

/**
 * Update user password
 * Returns structured result
 */
export const updatePassword = async (password: string) => {
  const { error } = await supabase.auth.updateUser({
    password,
  })

  return {
    success: !error,
    message: !error ? "Password updated successfully" : undefined,
    error: error?.message,
  }
}

/**
 * Extract Supabase auth confirmation token from URL
 * Used when processing Supabase confirmation links
 */
export function extractConfirmationTokenFromUrl() {
  // Check in URL search params first (traditional params)
  const urlParams = new URLSearchParams(window.location.search)
  const tokenFromParams =
    urlParams.get("token_hash") || urlParams.get("confirmation_token")
  if (tokenFromParams) return tokenFromParams

  // Check in URL hash params (used by Supabase invites)
  const hashParams = new URLSearchParams(window.location.hash.substring(1))
  return hashParams.get("token_hash") || hashParams.get("confirmation_token")
}

/**
 * Extract Supabase auth refresh token from URL hash
 * Used when processing Supabase invite links
 */
export function extractRefreshTokenFromUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  const refreshTokenFromParams = urlParams.get("refresh_token")
  if (refreshTokenFromParams) return refreshTokenFromParams

  const hashParams = new URLSearchParams(window.location.hash.substring(1))
  return hashParams.get("refresh_token")
}

export function isPendingReportShareAccountSetup(
  session: Session | null | undefined,
): boolean {
  if (!session?.user) return false

  const metadata = session.user.user_metadata || {}
  return (
    metadata.source === "report_share" &&
    metadata.account_setup_complete !== true
  )
}

/**
 * Handles authentication state changes related to invites
 * This can be integrated with the onAuthStateChange event listener
 */
export async function handleAuthStateChange(
  event: AuthChangeEvent,
  session: Session | null,
  onSuccess?: (organizationId: string) => void,
  onError?: (error: Error) => void,
) {
  // Only process invites on sign in or session refresh
  if (!session || (event !== "SIGNED_IN" && event !== "TOKEN_REFRESHED")) {
    return
  }

  try {
    // Check if there's a confirmation token in the URL (from Supabase email confirmation)
    const confirmationToken = extractConfirmationTokenFromUrl()
    if (confirmationToken) {
      // Let the Supabase trigger handle organization assignment
      // The database triggers will connect the user to the appropriate organization

      // Clear the token from the URL to prevent repeated processing
      const url = new URL(window.location.href)
      url.searchParams.delete("confirmation_token")
      window.history.replaceState({}, document.title, url.toString())

      // Call success callback if provided
      if (onSuccess) {
        // We don't have the organization ID here, but we can assume the user has been added to an organization
        // This will ensure the user is redirected to the dashboard
        onSuccess("organization_assigned_by_trigger")
      }
    }
  } catch (error) {
    // Call error callback if provided
    if (onError) {
      onError(error instanceof Error ? error : new Error(String(error)))
    }
  }
}
